import * as Colyseus from 'colyseus.js';
import api from '../services/api.js';
import { createVar } from '@src/utils/var.js';
import { noop, isPlainObject, percent, jsonParse, getQueryVariable } from '@src/utils/utils.js';

export const RequestStatus = {
  INITIAL: 0,
  LOADING: 1,
  LOADED: 2
};
// ^ in case of error, request is INITIAL
//   while error is a non-empty string

export const RoomStatus = {
  SCENARIO: 0,
  WAIT_START: 1,
  WAIT_NEXT: 2,
  WAIT_END: 3
};

// Always increase this number when making changes
// to the data structure stored in the localStorage
const STORAGE_VERSION = 2;
const STORAGE_RETENTION_MS =  2 * 7 * 24 * 60 * 60 * 1000;

let warnedAboutWakeLock_ = false;

function useNewAnswersFormat(answers) {
  if (!isPlainObject(answers)) return false;
  return Object.values(answers).every(entry => {
    return (
      entry.length === 2 &&
      Array.isArray(entry[0]) &&
      (entry[1] >= 0 && entry[1] <= 1)
    );
  });
}

function isValidScoreEntry(scoreEntry) {
  if (!isPlainObject(scoreEntry)) return false;
  const { sid, answers, score } = scoreEntry;
  if (!useNewAnswersFormat(answers)) return false;
  if (typeof sid !== 'string') return false;
  if (!Array.isArray(score)) return false;
  if (!Number.isFinite(score[0]) || score[0] < 0) return false;
  if (!Number.isInteger(score[1]) || score[1] < 0) return false;
  return true;
}

export class Store {
  constructor() {
    this.serverUrl = SERVER_URL;
    this.client = new Colyseus.Client(this.serverUrl);
    this.isTest = DEBUG && getQueryVariable('test');
    this.testEnd = 0;

    this.popup = createVar({
      id: '',
      content: null,
      active: false
    });

    this.user = createVar({
      status: RequestStatus.INITIAL,
      error: '',
      authenticating: true,
      data: null
    });

    this.navActive = createVar(false);

    // Code suggested after a disconnect
    this.suggestedCode = '';

    this.connection = createVar({
      status: RequestStatus.INITIAL,
      error: '',
      code: null,
      userName: null
    });
    this.room = null;
    this.sessionId = '';
    this.wakeLock = null;
    this.wakeLockTimer = null;

    // There is no way to distinguish an intentional user disconnect
    // from an unexpected server disconnect so we use this flag to
    // ignore errors when disconnecting
    this.ignoreNextDisconnect = false;

    this.delegates = createVar({
      total: 0,
      ready: 0
    });

    this.roomState = createVar({});

    this.answer = createVar({
      status: RequestStatus.INITIAL,
      error: ''
    });
    // History of given answers
    // "{taskId}.{activityId}": [answer, correct]
    this.answers = {};

    // This is the total score over any answer given,
    // including answers given for a same question
    this.score = createVar({
      correct: 0,
      total: 0
    });

    // Only updated once updateStats() is called
    // Used to delay animations in the score screen
    this.stats = createVar({
      numAsked: 0,
      numCorrect: 0,
      scoreRate: 0,
      groupScoreRate: 100
    });

    // completed scenario and score
    this.completion = createVar({
      title: '',
      correct: 0,
      total: 0
    });

    this.tabActive = createVar(false);
  }

  openPopup(id, content) {
    this.popup.setValue({ id, content, active: true });
  }

  closePopup(id) {
    const popup = this.popup.value;
    if (popup.active && (!id || popup.id === id)) {
      this.popup.setValue({ active: false }, true);
    }
  }

  isInAuth() {
    return this.user.value.authenticating;
  }

  isAuthenticated() {
    return !!this.user.value.data;
  }

  toggleNav(active = !this.navActive.value) {
    this.navActive.value = active;
  }

  isConnected() {
    return this.connection.value.status === RequestStatus.LOADED;
  }

  isInSession() {
    return this.isConnected() && !!this.sessionId;
  }

  getIdentity() {
    if (this.isAuthenticated()) {
      return this.user.value.data?.email || 'Unknown user';
    }
    if (this.isConnected()) {
      const { userName } = this.connection.value;
      return userName ? userName + ' (Guest)' : 'Guest';
    }
    return 'Guest';
  }

  init() {
    this.auth();
    this.cleanupStorage();
  }

  async auth() {
    this.user.setValue({ status: RequestStatus.LOADING }, true);

    let errorMsg = '';
    let user = null;
    try {
      const status = await api.getStatus();
      if (status.code === 0) {
        if (status.data.authenticated) {
          user = status.data.user;
        }
      } else {
        errorMsg = status.message;
      }
    } catch (err) {
      errorMsg = 'Failed to get authentication status';
      console.error(errorMsg + ':', err);
    }

    // If the user skept authentication while loading then ignore the result
    if (!this.isInAuth()) {
      this.user.setValue({
        status: RequestStatus.LOADED,
        error: ''
      }, true);
      return;
    }

    this.user.setValue({
      status: RequestStatus.LOADED,
      error: errorMsg,
      authenticating: !user,
      data: user
    }, true);

    if (this.isAuthenticated()) {
      this.onAuthDone();
    }
  }

  skipAuth() {
    if (this.isInAuth() && !this.isAuthenticated()) {
      this.user.setValue({ authenticating: false }, true);
      this.onAuthDone();
    }
  }

  getLoginUrl() {
    return SERVER_URL + '/api/login?postLoginUrl=' + encodeURIComponent(window.location.href);
  }

  async logout() {
    this.toggleNav(false);
    this.closePopup('logout');
    this.user.setValue({ status: RequestStatus.LOADING }, true);
    this.ignoreNextDisconnect = true;
    try {
      await this.disconnect();
    } catch (err) {
      console.error('Error while leaving room:', err);
      this.clear();
    }
    if (this.isAuthenticated()) {
      try {
        await api.logout();
      } catch (err) {
        console.error('Error while logging out:', err);
      }
    }
    this.user.value = {
      status: RequestStatus.LOADED,
      error: '',
      authenticating: true,
      data: null
    };
  }

  onAuthDone() {
    this.autoConnect();
  }

  async connect(code, userName) {
    if (this.connection.value.status !== RequestStatus.INITIAL) return;
    try {
      this.connection.setValue({ status: RequestStatus.LOADING }, true);
      await this.connectInternal(code, userName);
      if (this.connection.value.status === RequestStatus.LOADING) {
        this.connection.setValue({
          status: RequestStatus.LOADED,
          error: '',
          code,
          userName
        }, true);
        this.suggestedCode = code;
        this.setUrlCode(code);
      } else {
        // connection was aborted early
        await this.disconnect();
      }
    } catch (err) {
      const errCode = err.code;
      let errMsg = err.message;
      if (errCode === 4212) { // Invalid room code
        errMsg = 'Sorry that code is not valid. Please check you have entered it correctly!';
        this.suggestedCode = '';
        code = null;
        this.setUrlCode('');
      } else if (errCode === 4304) { // Username missing
        errMsg = '';
      } else if (errCode === 4305) { // Username taken
        errMsg = 'Sorry, that username is not valid. Please try a different username.';
      } else if (errCode === 4306) { // Username taken
        errMsg = 'Sorry, that username is already in use. Please try a different username.';
      } else if (errCode === 4307) { // Username obscene
        errMsg = 'That username is not permitted. Please try a different username.';
      } else if (errMsg === 'offline') {
        errMsg = 'Sorry, the server is currently unavailable.';
        code = this.connection.value.code;
      } else if (errCode === 401 && errMsg === 'User authentication failed') {
        // Bring the user back to the authentication screen.
        this.user.setValue({
          error: 'Sorry, the user authentication failed. Please login again.',
          authenticating: true,
          data: null
        }, true);
        errMsg = '';
        code = null;
      } else {
        errMsg = 'Sorry, an unexpected error occured.'
        code = this.connection.value.code;
        console.error(err);
      }
      this.connection.setValue({
        status: RequestStatus.INITIAL,
        error: errMsg,
        code,
        userName: null
      }, true);
    }
  }

  async connectInternal(code, userName) {
    this.client.auth.token = this.isAuthenticated() ? '.credentials' : null;
    const options = {};
    if (!this.isAuthenticated() && userName) {
      options.userName = userName;
    }
    const room = await this.client.joinById(code, options);

    room.onMessage('state', state => this.setRoomState(state));
    room.onMessage('result', result => this.onAnswerResult(result));
    room.onMessage('D', ([total, ready]) => this.delegates.value = { total, ready });
    room.onMessage('S', scoreEntry => this.onScoreEntry(scoreEntry));
    // room.onMessage('__playground_message_types', noop);
    room.onLeave((code) => {
      console.log(`Room onLeave (code: ${code})`);
      let message = '';
      if (code === 4000 && !this.ignoreNextDisconnect) {
        message = 'You have been disconnected.';
      } else if (code === 4308) { // User logged in on another device
        message = 'You have been disconnected because this account joined on another browser session.';
      }
      this.ignoreNextDisconnect = false;
      this.disconnect(message);
    });
    room.onError((code, message) => {
      console.log(`Room onError: ${message} (code: ${code})`);
      this.disconnect('The connection was closed due to an unexpected error.');
    });

    // Keep Alive pings
    // (Cloudflare closes WebSocket connections after 100 seconds of inactivity)
    const pingInterval = setInterval(() => {
      if (room.connection.isOpen) {
        room.send('P');
      } else {
        clearInterval(pingInterval);
      }
    }, 5000);

    this.room = room;
  }

  async disconnect(err) {
    await this.disconnectInternal();
    this.clear(err);
    this.setUrlCode('');
  }

  async disconnectInternal() {
    if (this.room) {
      try {
        this.room.leave();
      } catch (err) {
        console.error(err);
      }
    }
  }

  getUrlCode() {
    const code = window.location.pathname.substr(1).toUpperCase();
    return /^[A-Z0-9]{4}$/.test(code) ? code : '';
  }

  setUrlCode(code = '') {
    if (code.toUpperCase() !== this.getUrlCode()) {
      window.history.pushState({}, '', './' + code.toUpperCase());
    }
  }

  async autoConnect() {
    const urlCode = this.getUrlCode();
    const curCode = this.isConnected() ? this.room.id : '';
    // const curCode = this.isConnected() ? this.room.roomId : '';
    if (urlCode !== curCode) {
      if (curCode) {
        this.ignoreNextDisconnect = true;
        await this.disconnect();
      }
      if (urlCode) {
        this.suggestedCode = urlCode;
        await this.connect(urlCode);
      }
    }
  }

  async pingWakeLock() {
    if (!this.isConnected()) return;
    try {
      if (!this.wakeLock) {
        this.wakeLock = await navigator.wakeLock.request('screen');
        this.wakeLock.addEventListener('release', () => {
          if (DEV) console.log('WakeLock was released');
          if (this.wakeLockTimer) {
            clearTimeout(this.wakeLockTimer);
            this.wakeLockTimer = null;
          }
          this.wakeLock = null;
        });
        if (DEV) console.log('WakeLock acquired');
      }
      if (this.wakeLockTimer) {
        clearTimeout(this.wakeLockTimer);
      }
      this.wakeLockTimer = setTimeout(() => this.wakeLock.release(), 9e5);
    } catch (err) {
      if (!warnedAboutWakeLock_) {
        console.warn('WakeLock acquisition failed:', err);
        warnedAboutWakeLock_ = true;
      }
    }
  }

  setRoomState(roomState = {}) {
    const { sessionId = '' } = roomState;
    // flag the latest score as seen
    if (this.isInResult()) {
      this.updateStats();
    }
    const wasInScenario = this.roomState.value.status === RoomStatus.SCENARIO;
    const isInScenario = roomState.status === RoomStatus.SCENARIO;
    // update completion when leaving a completed scenario
    if (wasInScenario && !isInScenario) {
      this.updateCompletion();
    }
    // update room state
    this.roomState.value = roomState;
    if (sessionId !== this.sessionId) {
      this.sessionId = sessionId;
      this.clearSession();
      // attempt to retrieve existing session
      if (sessionId) this.load();
    } else if (this.isInQuestion()) {
      this.clearAnswer();
    }
    // collapse score tab
    this.toggleTab(false);
    this.pingWakeLock();
  }

  isInScenario() {
    if (!this.isConnected()) return false;
    const roomState = this.roomState.value;
    return roomState && roomState.status === RoomStatus.SCENARIO;
  }

  isLastActivityOfTask() {
    const roomState = this.roomState.value;
    return roomState && roomState.activityId >= roomState.activityCount - 1;
  }

  isInActivity() {
    if (!this.isInScenario()) return false;
    const roomState = this.roomState.value;
    return roomState.progress === 1 && roomState.taskProgress === 0;
  }

  isInQuestion() {
    return this.isInActivity() && this.roomState.value.activityProgress === 0;
  }

  isInResult() {
    return this.isInActivity() && this.roomState.value.activityProgress === 1;
  }

  isInScore() {
    if (!this.isInScenario()) return false;
    const roomState = this.roomState.value;
    return roomState.progress === 1 && roomState.taskProgress === 1 || roomState.taskProgress === 2;
  }

  getScreenKey() {
    if (!this.isInScenario()) return '';
    const roomState = this.roomState.value;
    const key = [roomState.progress];
    if (roomState.progress === 1) {
      key.push(roomState.taskId);
      if (roomState.taskProgress === 0) {
        key.push(roomState.activityId);
      }
    }
    return key.join('.');
  }

  getActivityKey() {
    if (!this.isInActivity()) return '';
    const roomState = this.roomState.value;
    return roomState.taskId + '.' + roomState.activityId;
  }

  getAnswer() {
    const activityKey = this.getActivityKey();
    if (!activityKey) return null;
    const entry = this.answers[activityKey];
    if (!entry || !Array.isArray(entry[0])) return null;
    return entry[0];
  }

  getScore(fullScenario = false) {
    let taskId = 255;
    let activityId = 255;
    if (!fullScenario) {
      const roomState = this.roomState.value;
      taskId = roomState.taskId;
      // NOTE: During the scenario outcome, taskId is -1
      //       but we still want to take all the tasks
      //       into accounts to compute the score.
      if (roomState.progress < 2) {
        taskId = roomState.taskId;
      }
      // NOTE: Same thing with the task outcome/outro.
      if (roomState.taskProgress === 0) {
        activityId = roomState.activityId;
      }
    }
    const correct = Object.keys(this.answers).reduce((ret, key) => {
      const [t, a] = key.split('.').map(e => parseInt(e));
      if (t < taskId || t === taskId && a <= activityId) {
        const entry = this.answers[key];
        if (entry[1] >= 0 && entry[1] <= 1) ret += entry[1];
      }
      return ret;
    }, 0);
    return !isNaN(correct) ? correct : this.score.value.correct;
  }

  // Receive score entry from instructor
  onScoreEntry(scoreEntry) {
    if (!isValidScoreEntry(scoreEntry)) {
      console.warn('Received invalid score entry from instructor:', scoreEntry);
      return;
    }
    const { sid, answers, score } = scoreEntry;
    if (sid !== this.sessionId) {
      console.warn(`Received score entry for session ${sid} but currently ` +
        (this.sessionId ? `in session ${this.sessionId}` : 'not in session'));
      return;
    }
    this.answers = answers;
    this.score.value = {
      correct: score[0],
      total: score[1]
    };
    if (this.isInResult() || this.isInScore()) {
      this.updateStats();
    }
    this.save();
  }

  submitAnswer(answer) {
    if (!this.isInSession() || !this.isInQuestion()) return;
    if (this.answer.value.status !== RequestStatus.INITIAL) return;

    this.submitAnswerInternal(this.sessionId, this.getActivityKey(), answer);
    this.answer.setValue({ status: RequestStatus.LOADING }, true);
  }

  submitAnswerInternal(sessionId, activityKey, answer) {
    if (!this.room) return;
    this.room.send('answer', { sid: sessionId, key: activityKey, data: answer });
  }

  onAnswerResult(result) {
    const { err, sid, key, answer, data } = result;
    if (sid !== this.sessionId) {
      console.warn('Result session ID mismatch: received %s, expected %s', sid, this.sessionId);
      return;
    }
    if (err === 0) {
      this.onAnswerSuccess(key, answer, data);
    } else {
      this.onAnswerError(key, answer, data);
    }
    this.pingWakeLock();
  }

  // The answer has been approved by the server,
  // it can now be stored locally in the answers history
  onAnswerSuccess(activityKey, answerData, answerScore) {
    if (activityKey === this.getActivityKey()) {
      this.answer.value = {
        status: RequestStatus.LOADED,
        error: ''
      };
    }
    this.answers[activityKey] = [answerData, answerScore];

    const score = this.score.value;
    this.score.value = {
      correct: score.correct + answerScore,
      total: score.total + 1
    };

    this.save();
  }

  onAnswerError(activityKey, answerData, error = '') {
    if (activityKey === this.getActivityKey()) {
      this.answer.value = {
        status: RequestStatus.INITIAL,
        error
      };
    }
    if (error) {
      console.error(
        'Answer error: %s\nactivity: %s\nanswer:',
        error, activityKey, answerData
      );
    }
  }

  clearAnswer() {
    this.answer.value = {
      status: RequestStatus.INITIAL,
      error: ''
    };
  }

  toggleTab(active = !this.tabActive.value) {
    this.tabActive.value = active;
  }

  updateStats() {
    const {
      scenarioActivityId = -1,
      groupScoreRate = 0
    } = this.roomState.value;
    const numAsked = scenarioActivityId + 1;
    const numCorrect = Math.min(this.getScore(), numAsked);

    const { correct, total } = this.score.value;
    const scoreRate = percent(correct / total);
    this.stats.value = {
      numAsked,
      numCorrect,
      scoreRate,
      groupScoreRate
    };

    if (DEV) {
      console.log('updateStats', this.stats.value.numAsked);
    }
  }

  clearScoreAndStats() {
    this.score.value = {
      correct: 0,
      total: 0
    };
    this.updateStats();
  }

  updateCompletion() {
    let title = '';
    let correct = 0;
    let total = 0;

    if (this.isInScenario()) {
      const roomState = this.roomState.value;
      if (roomState.completed) {
        title = roomState.scenarioTitle;
        total = roomState.scenarioActivityCount;
        correct = Math.min(this.getScore(true), total);
      }
    }
    this.completion.value = {
      title,
      correct,
      total
    };
  }

  clearSession() {
    this.clearAnswer();
    this.answers = {};
    this.clearScoreAndStats();
    this.toggleTab(false);
  }

  clear(error = '') {
    this.connection.value = {
      status: RequestStatus.INITIAL,
      error,
      code: null,
      userName: null
    };
    this.room = null;
    this.sessionId = '';

    this.delegates.value = {
      total: 0,
      ready: 0
    };

    this.roomState.value = {};

    this.clearSession();
  }

  async createTestRoom() {
    if (this.connection.value.status !== RequestStatus.INITIAL) return;
    this.testEnd = 0;
    try {
      this.client.auth.token = 'devtoken';
      this.connection.setValue({ status: RequestStatus.LOADING }, true);
      this.room = await this.client.create('scenario');
      this.testEnd = Date.now() + 9e5;
      this.room.onError(async (code, message) => {
        this.testEnd = 0;
        this.connection.setValue({ error: `${message} (${code})` }, true);
        try {
          await this.room.leave();
        } catch (err) {
          console.error(err);
        } finally {
          this.connection.value = {
            status: RequestStatus.INITIAL,
            error: message
          };
        }
      });
      this.room.onLeave((code) => {
        this.testEnd = 0;
        this.connection.value = {
          status: RequestStatus.INITIAL,
          error: `Disconnected (${code})`
        };
      });
      // this.room.onMessage('__playground_message_types', noop);
      this.connection.value = {
        status: RequestStatus.LOADED,
        error: ''
      };
    } catch (err) {
      this.connection.value = {
        status: RequestStatus.INITIAL,
        error: err.message
      };
      throw err;
    }
  }

  async closeTestRoom() {
    if (this.connection.value.status !== RequestStatus.LOADED || !this.room) return;
    this.testEnd = 0;
    try {
      this.connection.setValue({ status: RequestStatus.LOADING }, true);
      await this.room.leave();
      this.connection.value = {
        status: RequestStatus.INITIAL,
        error: ''
      };
    } catch (err) {
      this.connection.value = {
        status: RequestStatus.INITIAL,
        error: err.message
      };
      throw err;
    }
  }

  getTestTime() {
    if (this.testEnd === 0) return -1;
    return Math.max(Math.round(this.testEnd - Date.now()) / 1000, 0);
  }

  isStateRecoverable(state) {
    if (!this.isInSession()) return false;
    const { scenarioId, scenarioRevision } = this.roomState.value;
    return (
      state.ver === STORAGE_VERSION &&
      state.scenarioId === scenarioId &&
      state.scenarioRevision === scenarioRevision &&
      state.sessionId === this.sessionId &&
      state.score &&
      useNewAnswersFormat(state.answers)
    );
  }

  serialize() {
    if (!this.isInSession()) return '';
    const { scenarioId, scenarioRevision } = this.roomState.value;
    const state = {
      sessionId: this.sessionId,
      ver: STORAGE_VERSION,
      timestamp: Date.now(),
      scenarioId,
      scenarioRevision,
      score: this.score.value,
      answers: this.answers
    };
    return JSON.stringify(state);
  }

  deserialize(data) {
    let state;
    try {
      state = JSON.parse(data);
    } catch (err) {
      throw new Error('Invalid JSON');
    }
    if (this.isStateRecoverable(state)) {
      this.score.value = state.score;
      this.answers = state.answers;
    } else {
      throw new Error('State not recoverable');
    }
  }

  load() {
    if (this.isInSession() && localStorage) {
      const state = localStorage.getItem(this.sessionId);
      if (state) {
        try {
          this.deserialize(state);
        } catch (err) {
          console.warn('Failed to recover existing state: %s', err);
        }
      }
    }
  }

  save() {
    if (this.isInSession() && localStorage) {
      const data = this.serialize();
      localStorage.setItem(this.sessionId, data);
    }
  }

  cleanupStorage() {
    let removed = [];
    const now = Date.now();
    if (!localStorage) return removed;

    const numItems = localStorage.length;
    for (let i = numItems - 1; i >= 0; --i) {
      const sessionId = localStorage.key(i);
      const data = localStorage.getItem(sessionId);
      const session = jsonParse(data);
      const timestamp = session?.timestamp ?? 0;
      if (isNaN(timestamp) || now - timestamp > STORAGE_RETENTION_MS) {
        localStorage.removeItem(sessionId);
        removed.push(sessionId);
      }
    }
    return removed;
  }
}
